import {Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnInit, ViewChild} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {HubSettingsNgo, HubSettingsResponse} from '../../model/hub-session';
import {HubApiService} from '../../services/hub-api.service';
import {HelpTooltipComponent} from '../../components/help-tooltip/help-tooltip.component';
import '@web.awesome.me/webawesome-pro/dist/components/icon/icon.js';
@Component({
  selector: 'hub-settings',
  imports: [FormsModule, HelpTooltipComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SettingsComponent implements OnInit {
  settings: HubSettingsResponse | null = null;
  ngo: HubSettingsNgo | null = null;
  payrexxToken = '';
  dynamicsApiActionsSecret = '';
  logoFile: File | null = null;
  @ViewChild('logoInput') logoInput?: ElementRef<HTMLInputElement>;
  newLanguageCode = '';
  newLanguageName = '';
  loading = true;
  error = '';
  success = '';

  constructor(private hubApi: HubApiService) {
  }

  ngOnInit(): void {
    this.load();
  }

  saveSettings(): void {
    if (!this.ngo) return;
    this.clearMessages();
    this.hubApi.saveSettings({
      ...this.ngo,
      payrexxToken: this.payrexxToken,
      dynamicsApiActionsSecret: this.dynamicsApiActionsSecret
    }).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.payrexxToken = '';
        this.dynamicsApiActionsSecret = '';
        if (this.logoFile) {
          this.uploadLogo(this.logoFile);
        } else {
          this.success = this.t('HubSettingsSavedMessage');
        }
      },
      error: (error) => this.error = error.error?.message || this.t('HubSettingsSaveFailedMessage')
    });
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.logoFile = input.files?.[0] ?? null;
  }

  private uploadLogo(file: File): void {
    this.hubApi.uploadLogo(file).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.clearLogoSelection();
        this.success = this.t('HubSettingsSavedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubLogoUploadFailedMessage')
    });
  }

  private clearLogoSelection(): void {
    this.logoFile = null;
    if (this.logoInput) {
      this.logoInput.nativeElement.value = '';
    }
  }

  addLanguage(): void {
    this.clearMessages();
    this.hubApi.addLanguage(this.newLanguageCode.trim(), this.newLanguageName.trim()).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.newLanguageCode = '';
        this.newLanguageName = '';
        this.success = this.t('HubLanguageAddedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubLanguageAddFailedMessage')
    });
  }

  updateLanguage(code: string, name: string): void {
    this.clearMessages();
    this.hubApi.updateLanguage(code, name).subscribe({
      next: (settings) => this.applySettings(settings),
      error: (error) => this.error = error.error?.message || this.t('HubLanguageUpdateFailedMessage')
    });
  }

  deleteLanguage(code: string): void {
    if (!confirm(this.t('HubDeleteLanguageConfirmation').replace('{language}', code))) return;
    this.clearMessages();
    this.hubApi.deleteLanguage(code).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.success = this.t('HubLanguageDeletedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubLanguageDeleteFailedMessage')
    });
  }

  t(key: string): string {
    return this.hubApi.translate(key) || key;
  }

  private load(): void {
    this.hubApi.loadSettings().subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubSettingsLoadFailedMessage');
        this.loading = false;
      }
    });
  }

  private applySettings(settings: HubSettingsResponse): void {
    this.settings = settings;
    this.ngo = {...settings.ngo};
  }

  private clearMessages(): void {
    this.error = '';
    this.success = '';
  }
}
